import { useState } from 'react';

import { Text } from '@gympass/yoga';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatDate } from 'utils/helpers';

import { SpecialistType } from 'services/index';

import * as S from './style';

import { CriticalUserSectionProps } from './types';

import {
  DrawerCriticalityQualifiers,
  DrawerValidateCriticalityQualifiers,
} from '..';

const CriticalUserSection: React.FC<CriticalUserSectionProps> = ({
  criticality,
  cognitoId,
  verifyUserInfoCriticality,
  role,
}) => {
  const intl = useIntl();
  const [openDrawerCriticalityQualifiers, setOpenDrawerCriticalityQualifiers] =
    useState(false);

  const [
    openDrawerValidateCriticalityQualifiers,
    setOpenDrawerValidateCriticalityQualifiers,
  ] = useState(false);

  const handleOnCloseDrawer = () => {
    setOpenDrawerCriticalityQualifiers(false);
    setOpenDrawerValidateCriticalityQualifiers(false);
    verifyUserInfoCriticality();
  };

  const renderDrawers = () => {
    return (
      <>
        <DrawerCriticalityQualifiers
          openDrawer={openDrawerCriticalityQualifiers}
          onCloseDrawer={handleOnCloseDrawer}
        />
        <DrawerValidateCriticalityQualifiers
          openDrawer={openDrawerValidateCriticalityQualifiers}
          onCloseDrawer={handleOnCloseDrawer}
          cognitoId={cognitoId}
          confirmedWithExtUserPresent={criticality?.confirmedWithExtUserPresent}
          hasExtUsers={criticality?.hasExtUsers}
        />
      </>
    );
  };

  return (
    <>
      {renderDrawers()}
      <S.CriticalUserSection data-testid="criticalUserSection">
        <S.CriticalUserSectionHeader>
          <Text.H5 data-testid="criticalTitle">
            <FormattedMessage id="ENDUSER.SESSION.CRITICAL.TITLE" />
          </Text.H5>

          {role === SpecialistType.CP && (
            <S.ButtonValidate
              data-testid="criticalButtonValidate"
              onClick={() => {
                setOpenDrawerValidateCriticalityQualifiers(true);
              }}
            >
              <S.TextValidate data-testid="criticalButtonValidateText">
                <FormattedMessage
                  id={`ENDUSER.SESSION.CRITICAL.BUTTON.${
                    !criticality?.isCriticalityConfirmed
                      ? 'VALIDATE'
                      : 'VALIDATE.EDIT'
                  }`}
                />
              </S.TextValidate>
            </S.ButtonValidate>
          )}
        </S.CriticalUserSectionHeader>
        <S.StatusContainer>
          <S.CriticalBadge isCritical={criticality?.hasShownCriticalSigns}>
            <S.IsSignalCriticalText data-testid="criticalStatusText">
              <FormattedMessage
                id={`CAREMATRIXCPTC.CRITICALSTATUS.${
                  criticality?.hasShownCriticalSigns ? 'TRUE' : 'FALSE'
                }`}
              />
            </S.IsSignalCriticalText>
          </S.CriticalBadge>

          {!criticality?.isCriticalityConfirmed && (
            <S.StatusValidateContainer>
              <S.StatusValidateIcon />

              <S.StatusValidateText data-testid="criticalStatusValidateText">
                <FormattedMessage id="ENDUSER.SESSION.CRITICAL.STATUS.TEXT.FALSE" />
              </S.StatusValidateText>
            </S.StatusValidateContainer>
          )}
        </S.StatusContainer>
        <S.CriticalTextObservation data-testid="criticalObservationText">
          <FormattedMessage
            id={`ENDUSER.SESSION.CRITICAL.TEXT.OBSERVATION.${
              criticality?.hasShownCriticalSigns ? 'ISCRITICAL' : 'NONCRITICAL'
            }`}
            values={{
              a: (text: string) => (
                <S.CriticalTextObservationLink
                  onClick={() => {
                    setOpenDrawerCriticalityQualifiers(true);
                  }}
                >
                  {text}
                </S.CriticalTextObservationLink>
              ),
            }}
          />
        </S.CriticalTextObservation>

        {criticality?.isCriticalityConfirmed &&
          criticality?.hasShownCriticalSigns && (
            <S.StateOfLastForwardingContainer data-testid="criticalLastForwardingText">
              {criticality?.confirmedWithExtUserPresent ? (
                <S.ConfirmedAndForwarded>
                  <FormattedMessage
                    id="ENDUSER.SESSION.CRITICAL.TEXT.LASTFORWARDING.CONFIRMED"
                    values={{
                      dateLastForwarding: formatDate(criticality?.confirmedAt, {
                        locale: intl.locale,
                      }),
                      date: formatDate(criticality?.confirmedAt, {
                        locale: intl.locale,
                      }),
                    }}
                  />
                </S.ConfirmedAndForwarded>
              ) : (
                <S.ConfirmedAndNotForwarded>
                  <FormattedMessage
                    id="ENDUSER.SESSION.CRITICAL.TEXT.LASTFORWARDING.NOTCONFIRMED"
                    values={{
                      dateLastForwarding: formatDate(criticality?.confirmedAt, {
                        locale: intl.locale,
                      }),
                      date: formatDate(criticality?.confirmedAt, {
                        locale: intl.locale,
                      }),
                    }}
                  />
                </S.ConfirmedAndNotForwarded>
              )}
            </S.StateOfLastForwardingContainer>
          )}
      </S.CriticalUserSection>
    </>
  );
};

export default CriticalUserSection;
